// https://github.com/react-ga/react-ga/wiki/React-Router-v4-withTracker
import memoizeOne from 'memoize-one';
import React, { Component, useCallback, useEffect } from 'react';
import GoogleAnalytics from 'react-ga4';
import UAParser from 'ua-parser-js';

import { analyticsActions } from '../actions';
import useDispatch from '../hooks/useDispatch';
import useRegion from '../hooks/useRegion';
import { useMembership, useUser } from '../hooks/users';
import useSelector from '../hooks/useSelector';
import { AnalyticsState } from '../reducers/analytics.reducer';
import { Analytics } from '../shared/types';

// DataLouder - GA4
GoogleAnalytics.initialize('G-X66WZD4TDB');

const trackPage = (page: string) => {
  GoogleAnalytics.send({
    hitType: 'pageview',
    page,
  });
};

const withTracker = (WrappedComponent: React.ComponentType) => {
  // eslint-disable-next-line react/display-name
  return class extends Component {
    componentDidMount() {
      const page = window.location.pathname + window.location.search;
      trackPage(page);
    }

    componentDidUpdate() {
      const nextPage = window.location.pathname + window.location.search;

      trackPage(nextPage);
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  };
};

const parser = new UAParser();
const result = parser.getResult();

const getBrowserInfo = memoizeOne(() => {
  return {
    _browser: result.browser.name,
    _device: result.device.type,
    _os: result.os.name,
    _width: window.innerWidth,
  };
});

export const SHORT_UA =
  result.os.name +
  '/' +
  result.os.version +
  '/' +
  result.browser.name +
  '/' +
  result.browser.version;
export const isFirefox = () => getBrowserInfo()._browser?.includes('Firefox');
export const isPhoneOrTablet = () =>
  ['mobile', 'tablet'].includes(getBrowserInfo()._device);

// internal analytics action trackers
const useAnalytics = () => {
  const url = window.location.href;
  const user = useUser();
  const hasMembership = useMembership();
  const region = useRegion();

  const dispatch = useDispatch();

  const { actions } = useSelector(state => state.analytics as AnalyticsState);

  const queue = useCallback(
    (type: Analytics.ActionEnum, stockCode?: string, params?: any) => {
      if (user) {
        const time = Math.floor(Date.now() / 1000);
        dispatch(
          analyticsActions.queue({
            url,
            userId: user.id,
            vip: hasMembership,
            time,
            stockCode,
            region,
            action: type,
            ...getBrowserInfo(),
            params,
          })
        );
      }
    },
    [user, dispatch, url, hasMembership, region]
  );

  const flush = useCallback(() => {
    dispatch(analyticsActions.flush(actions));
  }, [actions, dispatch]);
  return [queue, flush];
};

const withAnalytics = (WrappedComponent: React.ComponentType) => {
  return function WithAnalytics(props: any) {
    const [queue, flush] = useAnalytics();

    return <WrappedComponent queue={queue} flush={flush} {...props} />;
  };
};

export interface AnalyticsProps {
  queue: (type: Analytics.ActionEnum, stockCode?: string, params?: any) => void;
  flush: () => void;
}

const usePageVisitAnalytics = (): void => {
  const [queue] = useAnalytics();

  useEffect(() => {
    queue(Analytics.ActionEnum.VisitPage);
  }, [queue]);
};

export { withTracker, useAnalytics, withAnalytics, usePageVisitAnalytics };
